import { Link } from 'react-router-dom';
import { Search, Menu, Phone, Home, Grid3X3, FileText, User } from 'lucide-react';
import { useState, useEffect } from 'react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMegaDropdownOpen, setIsMegaDropdownOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setIsMegaDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsMegaDropdownOpen(false);
    }, 200);
    setDropdownTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`} style={{transform: 'none'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
      <Link to="/" className="flex items-center space-x-2 cursor-pointer">
        <img 
          src="/favicon.ico" 
          alt="The Carrier Info Logo" 
          className="h-8 w-8 mr-2"
        />
        <span className="text-xl font-bold text-gray-900">The Carrier Info</span>
          </Link>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-6">
        <Link to="/" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
          Home
        </Link>
        <Link to="/search" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
          Search
        </Link>
        <Link to="/categories" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
          Categories
        </Link>
        <Link to="/validate" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
          Validation
        </Link>
        <Link to="/cariversity" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
          Carriversity
        </Link>
        
        {/* More Dropdown */}
        <div className="relative">
          <button
            className="text-gray-700 hover:text-amber-600 transition-colors font-medium flex items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            More
          </button>
          <div 
            className={`absolute top-full right-0 w-[800px] bg-white shadow-lg border rounded-lg p-8 mt-2 z-50 ${isMegaDropdownOpen ? 'block' : 'hidden'}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-12 col-lg">
                <div className="h6 flex items-center mb-2 mb-lg-3">
                  <div className="avatar avatar-sm flex-shrink-0 me-2">
                    <span className="avatar-initial rounded bg-label-primary w-8 h-8 flex items-center justify-center">
                      <Home className="w-4 h-4" />
                    </span>
                  </div>
                  <span className="ps-1 font-semibold text-gray-900">Portal</span>
                </div>
                <ul className="nav flex-column space-y-1">
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/login">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Login
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Dashboard
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/auth-register">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Register
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/truck/">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Dialler
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/trucking">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Series Dialer
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://dial.thecarrierinfo.com/US">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>US Map
                    </a>
                  </li>
                </ul>
              </div>
              <div className="col-12 col-lg">
                <div className="h6 flex items-center mb-2 mb-lg-3">
                  <div className="avatar avatar-sm flex-shrink-0 me-2">
                    <span className="avatar-initial rounded bg-label-primary w-8 h-8 flex items-center justify-center">
                      <Grid3X3 className="w-4 h-4" />
                    </span>
                  </div>
                  <span className="ps-1 font-semibold text-gray-900">Developers</span>
                </div>
                <ul className="nav flex-column space-y-1">
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/api-docs">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span><span>API Documentation</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://api.thecarrierinfo.com/analytics" target="_blank" rel="noopener noreferrer">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span><span>API Analytics</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://api.thecarrierinfo.com/usage" target="_blank" rel="noopener noreferrer">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span><span>Usage</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" href="https://api.thecarrierinfo.com/keys" target="_blank" rel="noopener noreferrer">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span><span>Auth Keys</span>
                    </a>
                  </li>
                </ul>
              </div>
              <div className="col-12 col-lg">
                <div className="h6 flex items-center mb-2 mb-lg-3">
                  <div className="avatar avatar-sm flex-shrink-0 me-2">
                    <span className="avatar-initial rounded bg-label-primary w-8 h-8 flex items-center justify-center">
                      <FileText className="w-4 h-4" />
                    </span>
                  </div>
                  <span className="ps-1 font-semibold text-gray-900">Carriversity</span>
                </div>
                <ul className="nav flex-column space-y-1">
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/cariversity">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>All Articles
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/cariversity/category/Introduction-to-Truck-Dispatching">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Introduction
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/cariversity/category/Regulations-and-Compliance">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Regulations
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/cariversity/category/Load-Management">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Load Management
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/cariversity/category/Technology-in-Truck-Dispatching">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Technology
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/seminars">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Seminars
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="col-12 col-lg">
                <div className="h6 flex items-center mb-2 mb-lg-3">
                  <div className="avatar avatar-sm flex-shrink-0 me-2">
                    <span className="avatar-initial rounded bg-label-primary w-8 h-8 flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </span>
                  </div>
                  <span className="ps-1 font-semibold text-gray-900">Others</span>
                </div>
                <ul className="nav flex-column space-y-1">
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/help">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Help
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/pricing">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Pricing
            </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/contact">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Contact US
            </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/about">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>About US
            </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/our-team">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Our Team
            </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link mega-dropdown-link flex items-center text-gray-800 hover:text-amber-600 py-1" to="/join">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>Join US
            </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Search Icon */}
        <Link
          to="/search"
          className="text-gray-700 hover:text-amber-600 transition-colors font-medium"
          aria-label="Search"
          title="Search"
        >
          <Search className="w-5 h-5" aria-hidden="true" />
        </Link>

        {/* Signup Button */}
        <Link
          to="https://dial.thecarrierinfo.com/login"
          className="inline-flex items-center justify-center text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary hover:bg-primary/90 h-10 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-2 rounded-full font-semibold"
        >
          <Phone className="w-4 h-4 mr-2" />
          Signup
        </Link>
          </div>

          {/* Mobile Menu Button */}
      <div className="md:hidden">
          <button
          className="text-gray-700 hover:text-amber-600 transition-colors" 
          aria-label="Toggle menu"
            onClick={toggleMenu}
          >
          <Menu className="w-7 h-7" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg border-t z-50">
          <div className="px-6 py-4 space-y-4">
            <Link to="/" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Home
            </Link>
            <Link to="/search" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Search
            </Link>
            <Link to="/categories" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Categories
            </Link>
            <Link to="/validate" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Validation
              </Link>
            <Link to="/cariversity" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Carriversity
              </Link>
            <Link to="/help" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Help
              </Link>
            <Link to="/pricing" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
              Pricing
              </Link>
            <Link to="/contact" className="block text-gray-700 hover:text-amber-600 transition-colors font-medium">
                Contact
              </Link>
              <Link 
              to="https://dial.thecarrierinfo.com/login"
              className="inline-flex items-center justify-center text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-2 rounded-full font-semibold mt-4"
              >
              <Phone className="w-4 h-4 mr-2" />
              Signup
              </Link>
          </div>
          </div>
        )}
        </div>
      </div>
    </nav>
  );
};

export default Header;